// ============================================================================
// CHAT RENDER PAYLOADS — the Flow chat card contract
// Tool results streamed over SSE may carry an optional `render` payload
// (result.render) describing a rich card. Unknown types / absent renders
// degrade to the plain ToolCallCard pill.
// ============================================================================

export const RENDER_STATUSES = ['created', 'updated', 'removed', 'failed'] as const
export type RenderStatus = (typeof RENDER_STATUSES)[number]

export type RenderChange = 'added' | 'removed' | 'moved' | null

/** The entity a render is about (workout, widget, doc, routine, ...). */
export interface RenderEntity {
  kind: string
  id: number | string | null
  title: string
  date: string | null
}

/** A render payload as emitted by the backend inside a tool result dict.
 *  `type` is intentionally an open string — unknown types must fall back to
 *  the ToolCallCard pill, so the frontend never narrows it exhaustively. */
export interface RenderPayload {
  type: string
  status: RenderStatus
  entity: RenderEntity
  spec: Record<string, unknown>
  /** Frontend-side annotation: the tool whose result carried this render.
   *  Not part of the backend contract. */
  tool?: string
}

// ----------------------------------------------------------------------------
// Per-type specs
// ----------------------------------------------------------------------------

export interface WorkoutBlockSpec {
  name: string
  summary: string
  zone: number | string | null
}

export interface WorkoutCardSpec {
  sport: string
  est_minutes: number
  blocks: WorkoutBlockSpec[]
  more_blocks?: number
  scheduled_date: string
}

export interface ChartSpec {
  widget_id: number | string
  title: string
}

export interface TrainingWeekSessionSpec {
  date: string
  title: string
  sport: string
  minutes: number
  change: RenderChange
}

export interface TrainingWeekSpec {
  week_start: string
  sessions: TrainingWeekSessionSpec[]
  reason?: string | null
}

export interface MemorySpec {
  content: string
  category: string
  old_value?: string | null
  action: 'set' | 'delete'
}

export interface RoutineInfoSpec {
  name?: string
  title?: string
  description?: string | null
  /** Human words, e.g. "Every Monday at 7am". */
  schedule?: string | null
  enabled?: boolean
  next_run_at?: string | null
}

export interface RoutineSpec {
  routine: RoutineInfoSpec
  event?: string
  run_summary?: string | null
}

export interface DocSectionPreview {
  heading: string
  teaser: string
}

export interface DocInfoSpec {
  id?: number | string | null
  path?: string
  title?: string
  doc_type?: string
  description?: string | null
  updated_at?: string | null
  /** Derived preview (backend): first section headings + a teaser line each. */
  sections?: DocSectionPreview[]
  section_count?: number
  word_count?: number
}

export interface DocSpec {
  doc: DocInfoSpec
  event?: string
  change_note?: string | null
}
